import { beginMapEdit } from './editlock';
// Markup: arrows, thrusts, callouts, numbered pins, freehand lines, boxes,
// ellipses, areas and notes drawn over a map to explain it — "this is the
// thrust", "this pocket is cut off", "front as of 14 Sep".
//
// ONE ENGINE FOR EVERY MAP. The cockpit and the Studio are both MapLibre maps,
// and markup is MapLibre layers installed on each (installMarkupLayers), so a
// shape looks, selects, moves and exports the same over a control map and
// over a satellite pair. The Studio used to draw its own markup on a 2D canvas
// — red only, lost when it closed, a box that was a screen rectangle — and the
// cockpit had this with no export; the two are one thing now.
//
// IT BELONGS TO A SHEET. Each shape is a row in `user_markup` (056), private to
// its author, on a `map_sheet` (059, sheets.ts) — the composition with the
// frame, layers, caption and export a finished map needs. Each map is bound to
// one sheet at a time (bindMarkupSheet). A cockpit bound to no sheet draws into
// the author's default sheet; a surface given an `ensure` callback creates its
// sheet on the first shape instead (the Studio's unsaved sheet).
//
// IT IS GEOGRAPHY, NOT PIXELS. Every shape is stored as real GeoJSON — the
// corners of a dragged box and the 64 points of an ellipse are unprojected at
// draw time — so it stays on the ground it was drawn around at every zoom, and
// the server sees a shape it could measure. The ARROWHEAD, the pin and the
// text are the things sized in pixels, deliberately: they are symbols, and a
// symbol that grows with zoom stops being one.
//
// IT WORKS LIKE A CANVAS (2026-09-29). Words are typed WHERE THEY GO — a caret
// on the map in the pen's own colour and size — where they used to be asked
// for by a browser prompt(). Words used before are offered as you type, with
// the colour they were drawn in, and on an empty box Enter reuses the newest:
// a sheet marks the same thing many times, and six oil slicks are six "Oil
// slick" callouts. A selection is a FRAME drawn in the DOM over the map, with
// handles — an arrow's or callout's two ends drag, a shape's corners scale it —
// where it used to be a yellow disc IN the canvas, 28 px across, on every note
// the moment it was drawn (a new shape is selected) and in any export taken
// while something was selected. Undo/redo, copy and paste at the pointer,
// Alt-drag to copy, a stamp that places a copy per click, arrow keys to nudge
// and one key per tool are the rest of what a hand that has used any drawing
// program reaches for.
//
// ONE EDITOR AT A TIME. The bar and the gestures attach to one map
// (openMarkup); every other map keeps rendering its own sheet.
//
// THE BAR IS THE PANEL. Markup used to be two floating pods — a tool rail on
// the left, which on the cockpit sat squarely over the live feed's drawer, and
// a sheet pod on the right — placed differently again in the Studio. It is now
// ONE bar docked under the top strip: the sheet it draws into, the tools, the
// pen, the selection, and the sheet's own flyout (sheets.ts) hanging off its
// right end. The cockpit moves it right of an open drawer (the selection
// card's rule), so it never covers the feed; the Studio mounts the same bar
// under its own top strip.

import * as maplibregl from 'maplibre-gl';

import { del, get, patch, post, type MarkupDto } from './api';
import { mercatorBearingDeg, type LngLat } from './geo';
import { toast } from './hud';
import './markup.css';

const SRC = 'user-markup';
const L_FILL = 'markup-fill';
const L_HATCH = 'markup-hatch';
const L_OUTLINE = 'markup-outline';
const L_OUTLINE_DASH = 'markup-outline-dash';
const L_LINE = 'markup-line';
const L_LINE_DASH = 'markup-line-dash';
const L_THRUST = 'markup-thrust';
const L_LEADER = 'markup-leader';
const L_HEAD = 'markup-head';
const L_ANCHOR = 'markup-anchor';
const L_PIN = 'markup-pin';
const L_PIN_TEXT = 'markup-pin-text';
const L_TEXT = 'markup-text';

/** Every markup layer, bottom to top. None of them draws the selection: that
 *  is DOM over the map, so it is never in an export. */
export const MARKUP_LAYER_IDS = [L_FILL, L_HATCH, L_OUTLINE, L_OUTLINE_DASH,
  L_LINE, L_LINE_DASH, L_THRUST, L_LEADER, L_HEAD, L_ANCHOR, L_PIN, L_PIN_TEXT, L_TEXT];
// What a click can pick a shape up by — the words and symbols first.
const CLICK_IDS = [L_TEXT, L_PIN_TEXT, L_PIN, L_ANCHOR, L_HEAD, L_LEADER, L_THRUST,
  L_LINE, L_LINE_DASH, L_OUTLINE, L_OUTLINE_DASH, L_FILL, L_HATCH];

export type MarkupTool = MarkupDto['kind'];
export type MarkupFill = MarkupDto['fill'];

/** The pen's palette. Exported because a sheet's legend names these
 *  colours — "red: destroyed, amber: damaged" — and the editor for that is
 *  built from the same list (sheets.ts). */
export const MARKUP_COLORS = ['#d93025', '#f4b860', '#2f6fed', '#00a86b', '#8e44ad', '#12202c', '#ffffff'];
const COLORS = MARKUP_COLORS;
const COLOR_KEY = 'cockpit-markup-color';
const WIDTH_KEY = 'cockpit-markup-width';
const DASH_KEY = 'cockpit-markup-dashed';
const FILL_KEY = 'cockpit-markup-fill';
const RECENT_KEY = 'cockpit-markup-labels';
const FILLS: MarkupFill[] = ['tint', 'solid', 'hatch', 'none'];
const FILL_LABELS: Record<MarkupFill, string> = {
  tint: '◧ Tint', solid: '■ Solid', hatch: '▨ Hatch', none: '□ No fill',
};
// The cockpit's glyphs come from OpenFreeMap, which serves the Noto family
// only: "Open Sans Semibold" 404s there, and a symbol layer asking for it
// draws no words at all (measured 2026-09-14).
const FONT = ['Noto Sans Bold'];
/** The same face for the DOM that must line up with the map's words — the box
 *  they are typed into, the frame around them. Noto Sans is rarely installed;
 *  Arial Bold sets within a few percent of the map's Noto Sans Bold, where the
 *  app's own Bahnschrift runs ~7% narrow and the frame clipped the last letter
 *  (measured 2026-09-29: "Front line 14 Sep" at 12.5 px — map ~99 px, Arial
 *  100.7, Bahnschrift 93.0). */
const LABEL_FONT = '"Noto Sans", Arial, sans-serif';

/** Sizes that follow the pen width, as [width, value, width, value] ramps: ONE
 *  definition for the layers and for the DOM drawn to match them. */
export const TEXT_SIZE = [1, 11, 12, 22];
const PIN_TEXT_SIZE = [1, 10, 12, 16];
const PIN_RADIUS = [1, 8, 12, 17];
const HEAD_SIZE = [1, 0.34, 12, 1.1];
/** The arrowhead image's edge in CSS pixels at icon-size 1 (44 px, pixelRatio 2). */
const HEAD_PX = 22;
/** MapLibre's text-max-width (ems) and line height, set explicitly on the
 *  layers so the typed box can wrap and stack the same way. */
const TEXT_MAX_EMS = 16;
const LINE_HEIGHT = 1.2;
/** The server's cap on a label. */
const LABEL_MAX = 200;

/** Below this a drag is a click: a stray press must not leave a dot behind. */
const DRAG_MIN_PX = 4;
/** Freehand sampling. Closer than this and a slow hand stores hundreds of
 *  points that draw the same line. */
const STROKE_STEP_PX = 3;
const ELLIPSE_STEPS = 64;
/** A callout CLICKED rather than dragged puts its label up and to the right. */
const CALLOUT_OFFSET = { x: 56, y: -40 };
/** A copy with nowhere better to go lands this far off, so it can be seen. */
const DUPLICATE_OFFSET = 24;
/** How far a click may miss a thin line and still pick it up. */
const HIT_PX = 5;
/** The selection frame's breathing room around what it frames. */
const FRAME_PAD = 5;
/** Arrow-key nudges, and how long a run of them waits before it is ONE move
 *  (one save, one undo step). */
const NUDGE_PX = 1;
const NUDGE_BIG_PX = 10;
const NUDGE_SETTLE_MS = 450;
const HISTORY_MAX = 100;
/** Labels remembered across sheets, and how many are offered at once. */
const RECENT_MAX = 24;
const SUGGEST_MAX = 6;

/** The kinds that carry words — typed in place, retyped by clicking again. */
const WORDY = new Set<MarkupTool>(['text', 'callout', 'pin']);
/** The kinds whose selection frame scales them from a corner. An arrow and a
 *  callout drag by their two ends instead; a note and a pin are sized by the
 *  pen width. */
const SCALABLE = new Set<MarkupTool>(['stroke', 'thrust', 'area', 'box', 'ellipse']);

type Px = { x: number; y: number };

/** Just enough of a MapLibre map to turn screen points into ground: the
 *  shape builders take this so they can be tested without a GL context. */
export interface Projector {
  unproject(point: [number, number]): { lng: number; lat: number };
}
/** Both ways — what moving a shape needs. */
export interface FullProjector extends Projector {
  project(ll: [number, number]): { x: number; y: number };
}

// Guarded, as readstate.ts guards its own keys: storage throws in a private
// window and is simply absent outside a browser.
function recall(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function remember(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* a preference, not data */ }
}

// The pen: what the next shape is drawn with, and what a selected shape is
// restyled to. Shared by every map — one hand holds one pen.
let color = recall(COLOR_KEY) || COLORS[0]!;
let width = Number(recall(WIDTH_KEY)) || 2.5;
let dashed = recall(DASH_KEY) === '1';
let fill: MarkupFill = (FILLS as string[]).includes(recall(FILL_KEY) ?? '')
  ? recall(FILL_KEY) as MarkupFill : 'tint';

const keyOfColor = (c: string): string => c.replace('#', '').toLowerCase();

/** What Ctrl+C holds: one shape, style and words and all. Module-wide, so a
 *  callout copied on one sheet pastes onto the next. */
let clipboard: MarkupDto | null = null;

// --- surfaces: one per map ------------------------------------------------------

type Fields = Partial<Pick<MarkupDto, 'geometry' | 'label' | 'color' | 'width' | 'dashed' | 'fill'>>;

/** One undoable step. Every one is a server write, undone by its inverse. */
type Op =
  | { t: 'create'; item: MarkupDto }
  | { t: 'delete'; item: MarkupDto }
  | { t: 'update'; id: string; before: Fields; after: Fields };

interface Surface {
  map: maplibregl.Map;
  items: MarkupDto[];
  draft: MarkupDto | null;
  selected: string | null;
  /** The shape whose words are being retyped: its label is left off the map
   *  while the typed box stands in for it. */
  editing: string | null;
  visible: boolean;
  sheetId: string | null;
  ensureSheet: (() => Promise<string | null>) | null;
  countListeners: ((n: number) => void)[];
  visibleListeners: ((on: boolean) => void)[];
  editorListeners: ((open: boolean) => void)[];
  seq: number;
  undo: Op[];
  redo: Op[];
  /** A shape brought back by undo is a NEW row with a new id, and the steps
   *  still on the stacks name the old one. */
  alias: Map<string, string>;
  /** Undo, redo and nudge saves run one after another, in the order pressed. */
  queue: Promise<unknown>;
  /** Pins posted but not yet back — a quick run of clicks must not hand two
   *  of them the same number. */
  pinsInFlight: number;
}

const surfaces = new Map<maplibregl.Map, Surface>();

function surfaceOf(map: maplibregl.Map): Surface {
  let s = surfaces.get(map);
  if (!s) {
    s = { map, items: [], draft: null, selected: null, editing: null, visible: true, sheetId: null,
          ensureSheet: null, countListeners: [], visibleListeners: [], editorListeners: [],
          seq: 0, undo: [], redo: [], alias: new Map(), queue: Promise.resolve(), pinsInFlight: 0 };
    surfaces.set(map, s);
    map.once('remove', () => {
      if (editor?.s.map === map) { editor.cleanup(); editor = null; }
      surfaces.delete(map);
    });
  }
  return s;
}

// --- geometry ---------------------------------------------------------------

const unproject = (map: Projector, p: Px): LngLat => {
  const ll = map.unproject([p.x, p.y]);
  return [ll.lng, ll.lat];
};

function mapCoords(g: GeoJSON.Geometry, f: (c: number[]) => number[]): GeoJSON.Geometry {
  switch (g.type) {
    case 'Point': return { type: 'Point', coordinates: f(g.coordinates) };
    case 'LineString': return { type: 'LineString', coordinates: g.coordinates.map(f) };
    case 'Polygon': return { type: 'Polygon', coordinates: g.coordinates.map((r) => r.map(f)) };
    default: return g;
  }
}

function coordsOf(g: GeoJSON.Geometry): number[][] {
  switch (g.type) {
    case 'Point': return [g.coordinates];
    case 'LineString': return g.coordinates;
    case 'Polygon': return g.coordinates[0] ?? [];
    default: return [];
  }
}

/** A dragged rectangle, unprojected corner by corner — so it is the shape that
 *  was dragged even on a rotated map, and real ground either way. */
export function boxRing(map: Projector, a: Px, b: Px): LngLat[] {
  const corners: Px[] = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
  const ring = corners.map((c) => unproject(map, c));
  return [...ring, ring[0]!];
}

/** An ellipse inscribed in the dragged box, as a polygon. Sampled on screen
 *  and unprojected, so it looks like the ellipse that was dragged. */
export function ellipseRing(map: Projector, a: Px, b: Px): LngLat[] {
  const cx = (a.x + b.x) / 2;
  const cy = (a.y + b.y) / 2;
  const rx = Math.abs(b.x - a.x) / 2;
  const ry = Math.abs(b.y - a.y) / 2;
  const ring: LngLat[] = [];
  for (let i = 0; i < ELLIPSE_STEPS; i++) {
    const t = (i / ELLIPSE_STEPS) * Math.PI * 2;
    ring.push(unproject(map, { x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) }));
  }
  return [...ring, ring[0]!];
}

/** A lasso's path, closed into a ring; null when it encloses nothing. */
export function lassoRing(path: LngLat[]): LngLat[] | null {
  const pts = path.filter((p, i) => i === 0 || p[0] !== path[i - 1]![0] || p[1] !== path[i - 1]![1]);
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (pts.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) pts.pop();
  if (pts.length < 3) return null;
  return [...pts, pts[0]!];
}

/** Chaikin corner-cutting on a screen path, both ends kept where the hand put
 *  them: a thrust is drawn by a wavering hand and should read as one sweep. */
export function smoothPath(pts: Px[], iterations = 2): Px[] {
  let cur = pts;
  for (let it = 0; it < iterations && cur.length > 2; it++) {
    const next: Px[] = [cur[0]!];
    for (let i = 0; i < cur.length - 1; i++) {
      const a = cur[i]!;
      const b = cur[i + 1]!;
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 },
                { x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    next.push(cur[cur.length - 1]!);
    cur = next;
  }
  return cur;
}

/** Every coordinate moved by a screen offset, through the projection — so a
 *  shape moves the way it looked like it moved, rotated map or not. */
export function translateGeometry(g: GeoJSON.Geometry, map: FullProjector,
                                  dx: number, dy: number): GeoJSON.Geometry {
  return mapCoords(g, (c) => {
    const p = map.project([c[0]!, c[1]!]);
    return unproject(map, { x: p.x + dx, y: p.y + dy });
  });
}

export interface Rect { x0: number; y0: number; x1: number; y1: number }

/** A shape's extent on screen, as it is projected now. */
export function screenBounds(g: GeoJSON.Geometry, map: FullProjector): Rect | null {
  const pts = coordsOf(g);
  if (!pts.length) return null;
  const r: Rect = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  for (const c of pts) {
    const p = map.project([c[0]!, c[1]!]);
    r.x0 = Math.min(r.x0, p.x);
    r.y0 = Math.min(r.y0, p.y);
    r.x1 = Math.max(r.x1, p.x);
    r.y1 = Math.max(r.y1, p.y);
  }
  return r;
}

/** Scale a shape on screen about `fixed` (the corner opposite the one held),
 *  so that the held corner `from` lands on `to`. Done in screen space through
 *  the projection, like a move, so it scales the way it looks. A flat side (a
 *  straight stroke has no height) keeps its size on that axis rather than
 *  dividing by nothing; `keepAspect` (Shift) scales both axes alike. */
export function scaleGeometry(g: GeoJSON.Geometry, map: FullProjector, fixed: Px, from: Px, to: Px,
                              keepAspect = false): GeoJSON.Geometry {
  const dx = from.x - fixed.x;
  const dy = from.y - fixed.y;
  let sx = Math.abs(dx) < 1 ? 1 : (to.x - fixed.x) / dx;
  let sy = Math.abs(dy) < 1 ? 1 : (to.y - fixed.y) / dy;
  if (keepAspect) {
    const k = Math.max(Math.abs(Math.abs(dx) < 1 ? 0 : sx), Math.abs(Math.abs(dy) < 1 ? 0 : sy)) || 1;
    sx = (sx < 0 ? -1 : 1) * k;
    sy = (sy < 0 ? -1 : 1) * k;
  }
  return mapCoords(g, (c) => {
    const p = map.project([c[0]!, c[1]!]);
    return unproject(map, { x: fixed.x + (p.x - fixed.x) * sx, y: fixed.y + (p.y - fixed.y) * sy });
  });
}

/** One vertex of a line put somewhere else — an arrow's tip, a callout's words. */
export function setVertex(g: GeoJSON.Geometry, index: number, to: LngLat): GeoJSON.Geometry {
  if (g.type !== 'LineString') return g;
  return { type: 'LineString', coordinates: g.coordinates.map((c, i) => (i === index ? [...to] : c)) };
}

/** Where a copy is held when it is put down: a note's or pin's own point, the
 *  thing a callout or arrow STARTS at, the middle of anything with an inside.
 *  So a pasted callout points at what the pointer is on. */
export function grabPoint(g: GeoJSON.Geometry, map: FullProjector): Px | null {
  if (g.type === 'Polygon') {
    const r = screenBounds(g, map);
    return r ? { x: (r.x0 + r.x1) / 2, y: (r.y0 + r.y1) / 2 } : null;
  }
  const first = coordsOf(g)[0];
  return first ? map.project([first[0]!, first[1]!]) : null;
}

/** A shape moved so its grab point sits at `at`. */
export function placeAt(g: GeoJSON.Geometry, map: FullProjector, at: Px): GeoJSON.Geometry {
  const from = grabPoint(g, map);
  return from ? translateGeometry(g, map, at.x - from.x, at.y - from.y) : g;
}

/** Which way a callout's words read: AWAY from the thing they point at. */
export function calloutAnchor(line: number[][]): 'left' | 'right' {
  const anchor = line[0];
  const at = line[line.length - 1];
  return anchor && at && at[0]! < anchor[0]! ? 'right' : 'left';
}

/** A new pin's label: one more than the highest numbered pin on the sheet. */
export function nextPinLabel(items: MarkupDto[]): string {
  let max = 0;
  for (const m of items) {
    if (m.kind !== 'pin') continue;
    const n = Number(m.label);
    if (Number.isInteger(n) && n > max) max = n;
  }
  return String(max + 1);
}

// --- words ------------------------------------------------------------------

/** A ramp read in code the way MapLibre reads it in the style: linear between
 *  the stops, clamped outside them. */
export function rampAt(stops: number[], w: number): number {
  const [w0, v0, w1, v1] = stops as [number, number, number, number];
  const t = Math.min(1, Math.max(0, (w - w0) / (w1 - w0)));
  return v0 + t * (v1 - v0);
}

/** How much room a label takes at a text size: its widest line, and its lines
 *  stacked — wrapped past TEXT_MAX_EMS the way the symbol layer wraps it.
 *  `measure` is the width of one line; a canvas in the app, a stub in tests. */
export function labelBox(label: string, size: number,
                         measure: (line: string) => number): { w: number; h: number } {
  const max = TEXT_MAX_EMS * size;
  let w = 0;
  let lines = 0;
  for (const line of label.split('\n')) {
    const lw = measure(line);
    lines += Math.max(1, Math.ceil(lw / max));
    w = Math.max(w, Math.min(lw, max));
  }
  return { w, h: lines * size * LINE_HEIGHT };
}

let measureCtx: CanvasRenderingContext2D | null | undefined;
function measureWith(size: number): (line: string) => number {
  if (measureCtx === undefined) measureCtx = document.createElement('canvas').getContext('2d');
  const ctx = measureCtx;
  if (!ctx) return (line) => line.length * size * 0.6;
  const font = `700 ${size}px ${LABEL_FONT}`;
  return (line) => {
    ctx.font = font;
    return ctx.measureText(line).width;
  };
}

/** A label used before, offered again with the look it had. */
export interface LabelPick { label: string; color: string; width: number; count: number }

/** What to offer while words are typed: labels this sheet already uses and
 *  ones used lately on any sheet, most recent first; filtered by what is typed
 *  (words that START with it ahead of words that merely contain it), never
 *  offering what is already typed exactly. A label on this sheet takes its
 *  look from its newest use here and counts its uses; pins are numbers, not
 *  words, and are left out. */
export function labelSuggestions(query: string, items: MarkupDto[], recent: LabelPick[],
                                 limit = SUGGEST_MAX): LabelPick[] {
  const keyOf = (label: string): string => label.trim().toLowerCase();
  const onSheet = new Map<string, LabelPick>();
  for (let i = items.length - 1; i >= 0; i--) {
    const m = items[i]!;
    if (m.kind !== 'text' && m.kind !== 'callout') continue;
    const label = (m.label ?? '').trim();
    if (!label) continue;
    const hit = onSheet.get(keyOf(label));
    if (hit) hit.count++;
    else onSheet.set(keyOf(label), { label, color: m.color, width: m.width, count: 1 });
  }
  const all: LabelPick[] = [];
  const seen = new Set<string>();
  for (const r of recent) {
    const key = keyOf(r.label);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    all.push(onSheet.get(key) ?? { ...r, label: r.label.trim(), count: 0 });
  }
  for (const [key, pick] of onSheet) {
    if (seen.has(key)) continue;
    seen.add(key);
    all.push(pick);
  }
  const q = keyOf(query);
  if (!q) return all.slice(0, limit);
  const starts: LabelPick[] = [];
  const contains: LabelPick[] = [];
  for (const p of all) {
    const k = keyOf(p.label);
    if (k === q) continue;
    if (k.startsWith(q)) starts.push(p);
    else if (k.includes(q)) contains.push(p);
  }
  return [...starts, ...contains].slice(0, limit);
}

function recallRecent(): LabelPick[] {
  try {
    const raw = JSON.parse(recall(RECENT_KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    const out: LabelPick[] = [];
    for (const r of raw as Record<string, unknown>[]) {
      if (r && typeof r.label === 'string' && typeof r.color === 'string' && typeof r.width === 'number') {
        out.push({ label: r.label, color: r.color, width: r.width, count: 0 });
      }
    }
    return out;
  } catch {
    return [];
  }
}

function rememberLabel(label: string, look: { color: string; width: number }): void {
  const key = label.trim().toLowerCase();
  const rest = recallRecent().filter((r) => r.label.trim().toLowerCase() !== key);
  const list = [{ label: label.trim(), ...look }, ...rest]
    .slice(0, RECENT_MAX).map((r) => ({ label: r.label, color: r.color, width: r.width }));
  remember(RECENT_KEY, JSON.stringify(list));
}

// --- images ----------------------------------------------------------------------

/** A solid triangle with its TIP at the top-centre of the image. Drawn once
 *  per colour and anchored 'top', so the tip sits exactly on the line's end
 *  and the body trails back along it once rotated. */
function drawArrowHead(paint: string): ImageData {
  const size = 44;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.beginPath();
  ctx.moveTo(size / 2, 1);
  ctx.lineTo(size - 4, size - 4);
  ctx.lineTo(size / 2, size * 0.72);
  ctx.lineTo(4, size - 4);
  ctx.closePath();
  ctx.fillStyle = paint;
  ctx.fill();
  // A hairline dark rim, so a white or pale head still reads on a bright
  // basemap (the same trick drawChip uses for marker chips).
  ctx.strokeStyle = 'rgba(16,24,32,.55)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

/** Diagonal hatching that tiles seamlessly (the period divides the tile). */
function drawHatch(paint: string): ImageData {
  const size = 24;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.strokeStyle = paint;
  ctx.globalAlpha = 0.85;
  ctx.lineWidth = 3;
  ctx.beginPath();
  for (let d = -size; d <= size * 2; d += 8) {
    ctx.moveTo(d, size);
    ctx.lineTo(d + size, 0);
  }
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

function ensureImages(map: maplibregl.Map, m: MarkupDto): void {
  const key = keyOfColor(m.color);
  if ((m.kind === 'arrow' || m.kind === 'thrust') && !map.hasImage(`mk-arrow-${key}`)) {
    map.addImage(`mk-arrow-${key}`, drawArrowHead(m.color), { pixelRatio: 2 });
  }
  if (m.fill === 'hatch' && !map.hasImage(`mk-hatch-${key}`)) {
    map.addImage(`mk-hatch-${key}`, drawHatch(m.color), { pixelRatio: 2 });
  }
}

// --- layers -----------------------------------------------------------------

type Expr = maplibregl.FilterSpecification;
const X = (e: unknown): Expr => e as Expr;
const isPoly = ['==', ['geometry-type'], 'Polygon'];
const isLine = ['==', ['geometry-type'], 'LineString'];
const isPoint = ['==', ['geometry-type'], 'Point'];
const isDashed = ['==', ['get', 'dashed'], 1];
const isSolid = ['!=', ['get', 'dashed'], 1];
const kindIs = (k: string): unknown[] => ['==', ['get', 'kind'], k];
const byWidth = (stops: number[]): never =>
  ['interpolate', ['linear'], ['get', 'width'], ...stops] as never;

/** Every markup layer, bottom to top. A pure function because MapLibre only
 *  validates a layer inside addLayer — during map construction, where one
 *  malformed expression takes the whole cockpit down with it. */
export function markupLayers(): maplibregl.LayerSpecification[] {
  const colour = ['get', 'color'] as never;
  return [{
    id: L_FILL, type: 'fill', source: SRC,
    filter: X(['all', isPoly, ['!=', ['get', 'fill'], 'hatch']]),
    paint: {
      'fill-color': colour,
      'fill-opacity': ['match', ['get', 'fill'], 'solid', 0.42, 'none', 0, 0.14] as never,
    },
  }, {
    id: L_HATCH, type: 'fill', source: SRC,
    filter: X(['all', isPoly, ['==', ['get', 'fill'], 'hatch']]),
    paint: { 'fill-pattern': ['concat', 'mk-hatch-', ['get', 'colorKey']] as never },
  }, {
    id: L_OUTLINE, type: 'line', source: SRC,
    filter: X(['all', isPoly, isSolid]),
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': colour, 'line-width': ['get', 'width'] as never },
  }, {
    id: L_OUTLINE_DASH, type: 'line', source: SRC,
    filter: X(['all', isPoly, isDashed]),
    layout: { 'line-join': 'round' },
    paint: { 'line-color': colour, 'line-width': ['get', 'width'] as never,
             'line-dasharray': [2, 1.6] },
  }, {
    id: L_LINE, type: 'line', source: SRC,
    filter: X(['all', isLine, ['match', ['get', 'kind'], ['thrust', 'callout'], false, true], isSolid]),
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': colour, 'line-width': ['get', 'width'] as never },
  }, {
    id: L_LINE_DASH, type: 'line', source: SRC,
    filter: X(['all', isLine, ['match', ['get', 'kind'], ['thrust', 'callout'], false, true], isDashed]),
    layout: { 'line-join': 'round' },
    paint: { 'line-color': colour, 'line-width': ['get', 'width'] as never,
             'line-dasharray': [2, 1.6] },
  }, {
    // A thrust is a broad translucent band: the axis of an advance, not a line.
    id: L_THRUST, type: 'line', source: SRC,
    filter: X(['all', isLine, kindIs('thrust')]),
    layout: { 'line-cap': 'butt', 'line-join': 'round' },
    paint: { 'line-color': colour, 'line-width': ['*', ['get', 'width'], 3.2] as never,
             'line-opacity': 0.8 },
  }, {
    id: L_LEADER, type: 'line', source: SRC,
    filter: X(['all', isLine, kindIs('callout')]),
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': colour, 'line-width': ['max', 1.2, ['*', ['get', 'width'], 0.6]] as never },
  }, {
    id: L_HEAD, type: 'symbol', source: SRC,
    filter: X(['==', ['get', 'head'], 1]),
    layout: {
      'icon-image': ['concat', 'mk-arrow-', ['get', 'colorKey']] as never,
      'icon-rotate': ['get', 'bearing'] as never,
      'icon-rotation-alignment': 'map',
      'icon-anchor': 'top',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      // Sized off the stroke width, so a fat arrow gets a fat head — and a
      // thrust's head spans its band.
      'icon-size': ['*', byWidth(HEAD_SIZE), ['match', ['get', 'kind'], 'thrust', 2.3, 1]] as never,
    },
  }, {
    // The dot on the thing a callout points at: small, it marks a spot.
    id: L_ANCHOR, type: 'circle', source: SRC,
    filter: X(['==', ['get', 'anchor'], 1]),
    paint: { 'circle-radius': 3, 'circle-color': colour,
             'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1 },
  }, {
    id: L_PIN, type: 'circle', source: SRC,
    filter: X(['all', isPoint, kindIs('pin')]),
    paint: {
      'circle-radius': byWidth(PIN_RADIUS),
      'circle-color': colour,
      'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.6,
    },
  }, {
    id: L_PIN_TEXT, type: 'symbol', source: SRC,
    filter: X(['all', isPoint, kindIs('pin')]),
    layout: {
      'text-field': ['get', 'label'] as never,
      'text-font': FONT,
      'text-size': byWidth(PIN_TEXT_SIZE),
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      // Dark numerals on the pale pens, white on the rest.
      'text-color': ['match', ['get', 'colorKey'], ['ffffff', 'f4b860'], '#12202c', '#ffffff'] as never,
    },
  }, {
    id: L_TEXT, type: 'symbol', source: SRC,
    filter: X(['any', ['all', isPoint, kindIs('text')], ['==', ['get', 'labelAt'], 1]]),
    layout: {
      'text-field': ['get', 'label'] as never,
      'text-font': FONT,
      'text-size': byWidth(TEXT_SIZE),
      // A callout's words read AWAY from the thing they point at, and a
      // block of lines is flush on that side — as the box they are typed in.
      'text-anchor': ['match', ['get', 'textAnchor'], 'right', 'right', 'left'] as never,
      'text-justify': 'auto',
      'text-offset': ['match', ['get', 'textAnchor'], 'right',
        ['literal', [-0.5, 0]], ['literal', [0.5, 0]]] as never,
      'text-max-width': TEXT_MAX_EMS,
      'text-line-height': LINE_HEIGHT,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': colour,
      'text-halo-color': 'rgba(12,18,26,.85)',
      'text-halo-width': 1.6,
    },
  }];
}

export function installMarkupLayers(map: maplibregl.Map): void {
  const s = surfaceOf(map);
  if (map.getSource(SRC)) return;
  map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  for (const layer of markupLayers()) map.addLayer(layer);
  applyVisibility(s);
  paint(s);
}

/** Put markup back on top — after a map added layers of its own (the Studio's
 *  scenes arrive long after the markup is installed). */
export function raiseMarkupLayers(map: maplibregl.Map): void {
  for (const id of MARKUP_LAYER_IDS) if (map.getLayer(id)) map.moveLayer(id);
}

function applyVisibility(s: Surface): void {
  for (const id of MARKUP_LAYER_IDS) {
    if (s.map.getLayer(id)) {
      s.map.setLayoutProperty(id, 'visibility', s.visible ? 'visible' : 'none');
    }
  }
}

export function setMarkupVisible(map: maplibregl.Map, on: boolean): void {
  const s = surfaceOf(map);
  if (s.visible === on) return;
  s.visible = on;
  applyVisibility(s);
  if (editor?.s === s) editor.refresh();
  for (const cb of s.visibleListeners) cb(on);
}

export const isMarkupVisible = (map: maplibregl.Map): boolean => surfaceOf(map).visible;
export const markupCount = (map: maplibregl.Map): number => surfaceOf(map).items.length;
export function onMarkupCountChange(map: maplibregl.Map, cb: (n: number) => void): void {
  surfaceOf(map).countListeners.push(cb);
}
export function onMarkupVisibleChange(map: maplibregl.Map, cb: (on: boolean) => void): void {
  surfaceOf(map).visibleListeners.push(cb);
}
/** The bar went up or came down on this map. The sheet flyout hangs off the
 *  bar, so sheets.ts closes it when the bar goes. */
export function onMarkupEditorChange(map: maplibregl.Map, cb: (open: boolean) => void): void {
  surfaceOf(map).editorListeners.push(cb);
}

// --- painting ---------------------------------------------------------------

/** A shape's features. `hideLabel` leaves its words off while they are being
 *  retyped in place. */
export function featuresOf(m: MarkupDto, hideLabel = false): GeoJSON.Feature[] {
  const props = {
    id: m.id, kind: m.kind, color: m.color, colorKey: keyOfColor(m.color),
    width: m.width, label: hideLabel ? '' : m.label ?? '', dashed: m.dashed ? 1 : 0,
    fill: m.fill ?? 'tint',
  };
  const feats: GeoJSON.Feature[] = [
    { type: 'Feature', properties: props, geometry: m.geometry as GeoJSON.Geometry },
  ];
  if (m.kind === 'arrow' || m.kind === 'thrust') {
    const line = (m.geometry as GeoJSON.LineString).coordinates as LngLat[];
    const tip = line[line.length - 1];
    const from = line[line.length - 2];
    if (tip && from) {
      feats.push({
        type: 'Feature',
        // The head points along the line AS DRAWN — the Mercator angle, which
        // is what the straight segment on screen actually has, not the
        // great-circle bearing that would leave a long arrow's head askew.
        properties: { ...props, head: 1, bearing: mercatorBearingDeg(from, tip) },
        geometry: { type: 'Point', coordinates: tip },
      });
    }
  }
  if (m.kind === 'callout') {
    const line = (m.geometry as GeoJSON.LineString).coordinates as LngLat[];
    const anchor = line[0];
    const at = line[line.length - 1];
    if (anchor && at && line.length >= 2) {
      feats.push({ type: 'Feature', properties: { ...props, anchor: 1 },
                   geometry: { type: 'Point', coordinates: anchor } });
      feats.push({ type: 'Feature',
                   properties: { ...props, labelAt: 1, textAnchor: calloutAnchor(line) },
                   geometry: { type: 'Point', coordinates: at } });
    }
  }
  return feats;
}

function paint(s: Surface): void {
  const map = s.map;
  let source: maplibregl.GeoJSONSource | undefined;
  try {
    source = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
  } catch {
    return;     // a removed map (the Studio closed) with a save still landing
  }
  if (!source) return;
  const list = s.draft ? [...s.items, s.draft] : s.items;
  const feats: GeoJSON.Feature[] = [];
  for (const m of list) {
    ensureImages(map, m);
    feats.push(...featuresOf(m, m.id === s.editing));
  }
  source.setData({ type: 'FeatureCollection', features: feats });
  if (editor?.s === s) editor.refresh();
}

function notifyCount(s: Surface): void {
  for (const cb of s.countListeners) cb(s.items.length);
}

/** Bind a map to a sheet and load its shapes. `null` is the author's default
 *  sheet — unless `ensure` is given, in which case null means a sheet that
 *  does not exist yet: nothing is loaded, and the first shape drawn calls
 *  `ensure` to create it. Resolves false when the sheet could not be read. */
export async function bindMarkupSheet(map: maplibregl.Map, sheetId: string | null,
                                      ensure?: () => Promise<string | null>): Promise<boolean> {
  const s = surfaceOf(map);
  if (typing?.s === s) finishTyping(false);
  if (nudging?.s === s) settleNudge();
  s.sheetId = sheetId;
  s.ensureSheet = ensure ?? null;
  s.selected = null;
  // Undo is about THIS sheet's shapes; another sheet's history is not ours.
  s.undo = [];
  s.redo = [];
  s.alias.clear();
  const my = ++s.seq;
  let ok = true;
  let items: MarkupDto[] = [];
  if (sheetId || !ensure) {
    try {
      const dto = await get<{ markup: MarkupDto[] }>(
        `/api/markup${sheetId ? `?sheet=${encodeURIComponent(sheetId)}` : ''}`);
      items = dto.markup;
    } catch {
      ok = false;     // a guest has no markup and no route to it; not an error
    }
  }
  if (my !== s.seq) return ok;
  s.items = items;
  paint(s);
  notifyCount(s);
  if (editor?.s === s) syncRail();
  return ok;
}

/** The sheet a map's shapes go to became real (created on first use) —
 *  record its id without reloading, since a shape may be mid-save. */
export function setMarkupSheetId(map: maplibregl.Map, sheetId: string): void {
  surfaceOf(map).sheetId = sheetId;
}

export const markupSheetId = (map: maplibregl.Map): string | null => surfaceOf(map).sheetId;

/** Kept for the cockpit's boot: load whatever sheet the map is bound to. */
export async function loadMarkup(map: maplibregl.Map): Promise<void> {
  const s = surfaceOf(map);
  await bindMarkupSheet(map, s.sheetId, s.ensureSheet ?? undefined);
}

// --- drawing ----------------------------------------------------------------

function geometryFor(map: FullProjector, kind: MarkupTool, start: Px, end: Px,
                     path: LngLat[], screen: Px[]): GeoJSON.Geometry | null {
  switch (kind) {
    case 'text':
    case 'pin':
      return { type: 'Point', coordinates: unproject(map, start) };
    case 'box':
      return { type: 'Polygon', coordinates: [boxRing(map, start, end)] };
    case 'ellipse':
      return { type: 'Polygon', coordinates: [ellipseRing(map, start, end)] };
    case 'area': {
      const ring = lassoRing(path);
      return ring ? { type: 'Polygon', coordinates: [ring] } : null;
    }
    case 'stroke':
      return path.length >= 2 ? { type: 'LineString', coordinates: path } : null;
    case 'thrust': {
      const smooth = smoothPath(screen).map((p) => unproject(map, p));
      return smooth.length >= 2 ? { type: 'LineString', coordinates: smooth } : null;
    }
    default:     // arrow, callout
      return { type: 'LineString', coordinates: [unproject(map, start), unproject(map, end)] };
  }
}

export function isDrawGesture(kind: MarkupTool, start: Px, end: Px, screen: Px[]): boolean {
  const samples = ['stroke', 'thrust', 'area'].includes(kind) ? screen : [end];
  return samples.some(p => Math.hypot(p.x - start.x, p.y - start.y) >= DRAG_MIN_PX);
}

// --- writes, and their undo ---------------------------------------------------

type ShapeBody = { kind: MarkupTool; geometry: GeoJSON.Geometry } & Fields;

const lookOf = (m: MarkupDto): Fields =>
  ({ color: m.color, width: m.width, dashed: m.dashed, fill: m.fill });

function pickFields(m: MarkupDto, keys: string[]): Fields {
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = (m as unknown as Record<string, unknown>)[k];
  return out as Fields;
}

function record(s: Surface, op: Op): void {
  s.undo.push(op);
  if (s.undo.length > HISTORY_MAX) s.undo.shift();
  s.redo = [];
  if (editor?.s === s) syncRail();
}

function enqueue(s: Surface, job: () => Promise<unknown>): void {
  s.queue = s.queue.then(job).catch(() => undefined);
}

const resolveId = (s: Surface, id: string): string => {
  let at = id;
  for (let i = 0; i < 1000 && s.alias.has(at); i++) at = s.alias.get(at)!;
  return at;
};

/** POST a shape to the surface's sheet (creating the sheet on first use).
 *  Null when it could not be saved — already said out loud. */
async function postShape(s: Surface, body: ShapeBody): Promise<MarkupDto | null> {
  try {
    let sheetId = s.sheetId;
    if (!sheetId && s.ensureSheet) {
      sheetId = await s.ensureSheet();
      if (!sheetId) return null;
      s.sheetId = sheetId;
    }
    return await post<MarkupDto>('/api/markup', {
      kind: body.kind, geometry: body.geometry, label: body.label ?? null,
      color: body.color ?? color, width: body.width ?? width,
      dashed: body.dashed ?? dashed, fill: body.fill ?? fill,
      ...(sheetId ? { sheetId } : {}),
    });
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not save that markup.');
    return null;
  }
}

/** A saved shape joins the sheet, selected while the editor is up. */
function adopt(s: Surface, made: MarkupDto): void {
  s.items = [...s.items, made];
  notifyCount(s);
  if (editor?.s === s) select(s, made.id);
  else paint(s);
}

async function createShape(s: Surface, body: ShapeBody): Promise<MarkupDto | null> {
  const made = await postShape(s, body);
  s.draft = null;
  if (!made) { paint(s); return null; }
  record(s, { t: 'create', item: made });
  adopt(s, made);
  return made;
}

/** A pin with the next number; a run of quick clicks counts the ones still
 *  on their way to the server. */
async function createPin(s: Surface, body: Omit<ShapeBody, 'kind' | 'label'>): Promise<void> {
  const label = String(Number(nextPinLabel(s.items)) + s.pinsInFlight);
  s.pinsInFlight++;
  try {
    await createShape(s, { ...body, kind: 'pin', label });
  } finally {
    s.pinsInFlight--;
  }
}

/** PATCH a shape. `before` is what it looked like before the change when the
 *  screen already shows the change (a drag); otherwise it is read off the
 *  shape. A failure puts the screen back. */
async function patchShape(s: Surface, id: string, body: Fields, before?: Fields,
                          track = true): Promise<boolean> {
  const prev = s.items.find((m) => m.id === id);
  if (!prev) return false;
  const keys = Object.keys(body);
  const was = before ?? pickFields(prev, keys);
  try {
    const updated = await patch<MarkupDto>(`/api/markup/${id}`, body);
    s.items = s.items.map((m) => (m.id === id ? updated : m));
    if (track) record(s, { t: 'update', id, before: was, after: pickFields(updated, keys) });
    return true;
  } catch (err) {
    s.items = s.items.map((m) => (m.id === id ? { ...m, ...was } : m));
    toast(err instanceof Error ? err.message : 'Could not change that markup.');
    return false;
  } finally {
    paint(s);
    if (editor?.s === s) syncRail();
  }
}

async function removeShape(s: Surface, id: string, track = true): Promise<boolean> {
  const item = s.items.find((m) => m.id === id);
  if (!item) return false;
  try {
    await del(`/api/markup/${id}`);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not delete that markup.');
    return false;
  }
  s.items = s.items.filter((m) => m.id !== id);
  if (track) record(s, { t: 'delete', item });
  if (s.selected === id) select(s, null);
  else paint(s);
  notifyCount(s);
  return true;
}

/** One step back (or forward). A step that can no longer be applied — its
 *  shape is gone — is dropped rather than left to jam the stack. */
async function step(s: Surface, back: boolean): Promise<void> {
  const op = (back ? s.undo : s.redo).pop();
  if (!op) return;
  let ok: boolean;
  if (op.t === 'update') {
    const id = resolveId(s, op.id);
    ok = await patchShape(s, id, back ? op.before : op.after, back ? op.after : op.before, false);
    if (ok && editor?.s === s) select(s, id);
  } else if ((op.t === 'create') === back) {
    ok = await removeShape(s, resolveId(s, op.item.id), false);
  } else {
    const m = op.item;
    const made = await postShape(s, { kind: m.kind, geometry: m.geometry as GeoJSON.Geometry,
                                      label: m.label, ...lookOf(m) });
    ok = !!made;
    if (made) {
      s.alias.set(resolveId(s, m.id), made.id);
      adopt(s, made);
    }
  }
  if (ok) (back ? s.redo : s.undo).push(op);
  if (editor?.s === s) syncRail();
}

function history(s: Surface, back: boolean): void {
  settleNudge();
  enqueue(s, () => step(s, back));
}

// --- nudging ------------------------------------------------------------------

let nudging: { s: Surface; id: string; orig: GeoJSON.Geometry; timer: ReturnType<typeof setTimeout> } | null = null;

function nudge(s: Surface, dx: number, dy: number): void {
  const item = s.items.find((m) => m.id === s.selected);
  if (!item) return;
  if (nudging && (nudging.s !== s || nudging.id !== item.id)) settleNudge();
  if (!nudging) {
    nudging = { s, id: item.id, orig: item.geometry as GeoJSON.Geometry,
                timer: setTimeout(settleNudge, NUDGE_SETTLE_MS) };
  }
  clearTimeout(nudging.timer);
  nudging.timer = setTimeout(settleNudge, NUDGE_SETTLE_MS);
  const g = translateGeometry(item.geometry as GeoJSON.Geometry, s.map, dx, dy);
  s.items = s.items.map((m) => (m.id === item.id ? { ...m, geometry: g } : m));
  paint(s);
}

function settleNudge(): void {
  const n = nudging;
  if (!n) return;
  nudging = null;
  clearTimeout(n.timer);
  const now = n.s.items.find((m) => m.id === n.id);
  if (!now) return;
  const geometry = now.geometry as GeoJSON.Geometry;
  enqueue(n.s, () => patchShape(n.s, n.id, { geometry }, { geometry: n.orig }));
}

// --- selection --------------------------------------------------------------

function select(s: Surface, id: string | null): void {
  s.selected = id;
  paint(s);
  if (editor?.s === s) syncRail();
}

async function restyle(s: Surface, body: Fields): Promise<void> {
  if (!s.selected) return;
  await patchShape(s, s.selected, body);
}

/** The rectangle a shape's WORDS take on screen — what a click grabs a note
 *  by, and the part of a callout that is not its leader. */
function wordsRect(map: FullProjector, m: MarkupDto): Rect | null {
  const g = m.geometry as GeoJSON.Geometry;
  const pts = coordsOf(g);
  if (m.kind === 'pin' && g.type === 'Point') {
    const p = map.project([pts[0]![0]!, pts[0]![1]!]);
    const r = rampAt(PIN_RADIUS, m.width) + 1.6;
    return { x0: p.x - r, y0: p.y - r, x1: p.x + r, y1: p.y + r };
  }
  if (m.kind !== 'text' && m.kind !== 'callout') return null;
  const at = m.kind === 'callout' ? pts[pts.length - 1] : pts[0];
  if (!at) return null;
  const p = map.project([at[0]!, at[1]!]);
  const size = rampAt(TEXT_SIZE, m.width);
  const { w, h } = labelBox(m.label ?? '', size, measureWith(size));
  const off = 0.5 * size;
  const x0 = m.kind === 'callout' && calloutAnchor(pts) === 'right' ? p.x - off - w : p.x + off;
  return { x0, y0: p.y - h / 2, x1: x0 + w, y1: p.y + h / 2 };
}

/** What the selection frame goes around: the shape as drawn, its stroke, head
 *  and words included. */
function frameOf(map: FullProjector, m: MarkupDto): Rect | null {
  if (m.kind === 'text' || m.kind === 'pin') return wordsRect(map, m);
  const b = screenBounds(m.geometry as GeoJSON.Geometry, map);
  if (!b) return null;
  let pad = m.width / 2;
  if (m.kind === 'thrust') pad = m.width * 1.6;
  if (m.kind === 'arrow' || m.kind === 'thrust') {
    pad = Math.max(pad, (HEAD_PX * rampAt(HEAD_SIZE, m.width) * (m.kind === 'thrust' ? 2.3 : 1)) / 2);
  }
  if (m.kind === 'callout') pad = 4;
  const r = { x0: b.x0 - pad, y0: b.y0 - pad, x1: b.x1 + pad, y1: b.y1 + pad };
  const words = m.kind === 'callout' ? wordsRect(map, m) : null;
  if (words) {
    r.x0 = Math.min(r.x0, words.x0);
    r.y0 = Math.min(r.y0, words.y0);
    r.x1 = Math.max(r.x1, words.x1);
    r.y1 = Math.max(r.y1, words.y1);
  }
  return r;
}

/** A grip on the selection: one END of an arrow or callout, which drags on its
 *  own, or a CORNER of a shape's frame, which scales it about the opposite
 *  corner. `at` is where it is shown; a corner's `from`/`fixed` are the
 *  shape's own corners, which the scaling is measured against. */
export type Handle =
  | { kind: 'vertex'; index: number; at: Px }
  | { kind: 'corner'; corner: number; at: Px; from: Px; fixed: Px };

export function handlesOf(m: MarkupDto, map: FullProjector, frame: Rect | null = null): Handle[] {
  const g = m.geometry as GeoJSON.Geometry;
  if ((m.kind === 'arrow' || m.kind === 'callout') && g.type === 'LineString') {
    const last = g.coordinates.length - 1;
    if (last < 1) return [];
    return [0, last].map((index) => {
      const c = g.coordinates[index]!;
      return { kind: 'vertex', index, at: map.project([c[0]!, c[1]!]) };
    });
  }
  if (!SCALABLE.has(m.kind)) return [];
  const b = screenBounds(g, map);
  if (!b || (b.x1 - b.x0 < 2 && b.y1 - b.y0 < 2)) return [];
  const f = frame ?? b;
  const own: Px[] = [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }];
  const shown: Px[] = [{ x: f.x0, y: f.y0 }, { x: f.x1, y: f.y0 }, { x: f.x1, y: f.y1 }, { x: f.x0, y: f.y1 }];
  return own.map((from, corner) => ({
    kind: 'corner', corner, at: shown[corner]!, from, fixed: own[(corner + 2) % 4]!,
  }));
}

const inside = (r: Rect, p: Px, pad = 0): boolean =>
  p.x >= r.x0 - pad && p.x <= r.x1 + pad && p.y >= r.y0 - pad && p.y <= r.y1 + pad;

// --- words typed in place -------------------------------------------------------

type Look = { color: string; width: number };

interface Typing {
  s: Surface;
  wrap: HTMLElement;
  input: HTMLTextAreaElement;
  list: HTMLElement;
  /** Where the words sit on the ground, and which way they read from it. */
  at: LngLat;
  align: 'left' | 'right' | 'center';
  sizes: number[];
  /** The shape being retyped; null for words that are not a shape yet. */
  itemId: string | null;
  suggest: boolean;
  picks: LabelPick[];
  active: number;
  /** How the words look: the pen's, a reused label's, or the shape's own. */
  look: Look;
  /** Called once: the words (null when given up) and how they looked. */
  done: (label: string | null, look: Look) => void;
}

let typing: Typing | null = null;

/** Size, colour and place the box over the map where the words will be drawn:
 *  the same face, size and anchor as the symbol layer, so finishing swaps the
 *  box for the words without anything jumping. */
function layoutTyping(t: Typing): void {
  const { input, wrap } = t;
  const size = rampAt(t.sizes, t.look.width);
  input.style.font = `700 ${size}px/${LINE_HEIGHT} ${LABEL_FONT}`;
  input.style.color = t.look.color;
  // The darkest pen's caret would vanish into the halo behind it.
  input.style.caretColor = keyOfColor(t.look.color) === '12202c' ? '#ffffff' : t.look.color;
  input.style.textAlign = t.align;
  const box = labelBox(input.value || input.placeholder || ' ', size, measureWith(size));
  input.style.width = `${Math.ceil(Math.max(box.w, size)) + 14}px`;
  input.style.height = '0px';
  input.style.height = `${input.scrollHeight + 2}px`;
  const p = t.s.map.project(t.at);
  const w = input.offsetWidth;
  const h = input.offsetHeight;
  const off = 0.5 * size;
  const inset = 5;    // the box's border and padding, before the first letter
  const x = t.align === 'left' ? p.x + off - inset
    : t.align === 'right' ? p.x - off - w + inset : p.x - w / 2;
  wrap.style.transform = `translate(${Math.round(x)}px, ${Math.round(p.y - h / 2)}px)`;
}

function renderPicks(t: Typing): void {
  const { list, picks } = t;
  list.replaceChildren();
  list.hidden = !picks.length;
  if (!picks.length) return;
  const head = document.createElement('div');
  head.className = 'mk-sug-head';
  head.textContent = t.active >= 0
    ? 'Used before · Enter reuses the marked one'
    : 'Used before · ↑↓ choose · Tab fills';
  list.append(head);
  picks.forEach((p, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.tabIndex = -1;
    b.className = `mk-sug${i === t.active ? ' on' : ''}`;
    b.dataset.i = String(i);
    b.setAttribute('role', 'option');
    b.setAttribute('aria-selected', String(i === t.active));
    b.title = t.itemId ? p.label : `${p.label} — in the colour and size it was drawn in`;
    const dot = document.createElement('span');
    dot.className = 'mk-sug-dot';
    dot.style.background = p.color;
    const words = document.createElement('span');
    words.className = 'mk-sug-t';
    words.textContent = p.label.replace(/\n+/g, ' / ');
    b.append(dot, words);
    if (p.count > 1) {
      const n = document.createElement('span');
      n.className = 'mk-sug-n';
      n.textContent = `×${p.count}`;
      n.title = 'Times on this sheet';
      b.append(n);
    }
    if (i === t.active) {
      const k = document.createElement('kbd');
      k.textContent = '↵';
      b.append(k);
    }
    list.append(b);
  });
}

function suggestFor(t: Typing): void {
  if (!t.suggest) return;
  t.picks = labelSuggestions(t.input.value, t.s.items, recallRecent());
  // On a NEW mark's empty box the newest label is marked, so Enter reuses it:
  // marking the same thing again is a drag and a key. Retyped words emptied
  // mean the mark should go, so nothing is marked there.
  t.active = !t.itemId && !t.input.value.trim() && t.picks.length ? 0 : -1;
  renderPicks(t);
}

/** The look of NEW words changed (a reused label, the pen): the typed box and
 *  a callout's leader follow. Retyped words keep their shape's look, which the
 *  pen restyles through the selection. */
function restyleTyping(t: Typing, look: Partial<Look>): void {
  t.look = { ...t.look, ...look };
  if (!t.itemId && t.s.draft) t.s.draft = { ...t.s.draft, ...t.look };
  paint(t.s);
  layoutTyping(t);
}

function acceptPick(t: Typing, p: LabelPick, finish: boolean): void {
  t.input.value = p.label;
  if (!t.itemId) restyleTyping(t, { color: p.color, width: p.width });
  if (finish) { finishTyping(true); return; }
  suggestFor(t);
  layoutTyping(t);
  t.input.setSelectionRange(p.label.length, p.label.length);
}

function onTypingKey(t: Typing, ev: KeyboardEvent): void {
  switch (ev.key) {
    case 'Enter': {
      if (ev.shiftKey || ev.isComposing) return;    // a new line; an IME choosing a word
      ev.preventDefault();
      const p = t.active >= 0 ? t.picks[t.active] : undefined;
      if (p) acceptPick(t, p, true);
      else finishTyping(true);
      return;
    }
    case 'Escape':
      ev.preventDefault();
      ev.stopPropagation();
      finishTyping(false);
      return;
    case 'Tab': {
      const p = t.picks[Math.max(0, t.active)];
      if (!p) return;
      ev.preventDefault();
      acceptPick(t, p, false);
      return;
    }
    case 'ArrowDown':
    case 'ArrowUp': {
      const n = t.picks.length;
      if (!n) return;
      ev.preventDefault();
      t.active = ev.key === 'ArrowDown' ? (t.active + 1) % n : (t.active <= 0 ? n - 1 : t.active - 1);
      renderPicks(t);
      return;
    }
  }
}

function startTyping(s: Surface, o: {
  at: LngLat; kind: 'text' | 'callout' | 'pin'; align: Typing['align']; look: Look;
  itemId?: string; value?: string; done: Typing['done'];
}): void {
  finishTyping(true);
  const wrap = document.createElement('div');
  wrap.className = `mk-type ${o.align}`;
  const input = document.createElement('textarea');
  input.className = 'mk-type-in';
  input.rows = 1;
  input.maxLength = LABEL_MAX;
  input.placeholder = o.kind === 'pin' ? '' : 'Type…';
  input.setAttribute('aria-label', o.kind === 'pin' ? 'Pin label' : 'Words');
  input.value = o.value ?? '';
  const list = document.createElement('div');
  list.className = 'mk-suggest';
  list.hidden = true;
  list.setAttribute('role', 'listbox');
  wrap.append(input, list);
  s.map.getContainer().appendChild(wrap);
  const t: Typing = {
    s, wrap, input, list, at: o.at, align: o.align,
    sizes: o.kind === 'pin' ? PIN_TEXT_SIZE : TEXT_SIZE,
    itemId: o.itemId ?? null, suggest: o.kind !== 'pin', picks: [], active: -1,
    look: o.look, done: o.done,
  };
  typing = t;
  s.editing = t.itemId;
  paint(s);    // leaves the words being retyped, and their frame, off the map
  input.addEventListener('input', () => { suggestFor(t); layoutTyping(t); });
  input.addEventListener('keydown', (ev) => onTypingKey(t, ev));
  input.addEventListener('blur', (ev) => {
    if (typing !== t) return;
    // Another window took focus — a label being copied from elsewhere. The
    // words are not finished; focus comes back to them.
    if (!document.hasFocus()) return;
    // The pen restyles the words being typed, and focus comes back to them.
    const to = ev.relatedTarget as Node | null;
    if (to && (editor?.rail.querySelector('.mk-pen')?.contains(to) || wrap.contains(to))) return;
    finishTyping(true);
  });
  // A suggestion is chosen without taking focus from the words.
  list.addEventListener('mousedown', (ev) => ev.preventDefault());
  list.addEventListener('pointerdown', (ev) => ev.stopPropagation());
  list.addEventListener('click', (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLElement>('[data-i]');
    const p = b ? t.picks[Number(b.dataset.i)] : undefined;
    if (!p) return;
    ev.stopPropagation();
    acceptPick(t, p, true);
  });
  suggestFor(t);
  layoutTyping(t);
  input.focus({ preventScroll: true });
  input.select();
}

function finishTyping(commit: boolean): void {
  const t = typing;
  if (!t) return;
  typing = null;
  const label = t.input.value.trim();
  t.wrap.remove();
  t.s.editing = null;
  paint(t.s);
  t.done(commit ? label : null, t.look);
}

const refocusTyping = (): void => { typing?.input.focus({ preventScroll: true }); };

function newText(s: Surface, p: Px): void {
  finishTyping(true);
  select(s, null);
  const at = unproject(s.map, p);
  startTyping(s, {
    at, kind: 'text', align: 'left', look: { color, width },
    done: (label, look) => {
      if (!label) return;
      rememberLabel(label, look);
      const geometry: GeoJSON.Point = { type: 'Point', coordinates: at };
      // Drawn at once; the draft gives way to the saved note.
      s.draft = { id: '', kind: 'text', geometry, label, ...look, dashed, fill, createdAt: '', updatedAt: '' };
      paint(s);
      void createShape(s, { kind: 'text', geometry, label, ...look });
    },
  });
}

function newCallout(s: Surface, geometry: GeoJSON.LineString): void {
  finishTyping(true);
  select(s, null);
  const coords = geometry.coordinates as LngLat[];
  s.draft = { id: '', kind: 'callout', geometry, label: null, color, width, dashed, fill,
              createdAt: '', updatedAt: '' };
  startTyping(s, {
    at: coords[coords.length - 1]!, kind: 'callout', align: calloutAnchor(coords),
    look: { color, width },
    done: (label, look) => {
      if (!label) { s.draft = null; paint(s); return; }
      rememberLabel(label, look);
      if (s.draft) s.draft = { ...s.draft, label, ...look };
      paint(s);
      void createShape(s, { kind: 'callout', geometry, label, ...look });
    },
  });
}

/** Retype a note's, callout's or pin's words where they stand. Emptying a
 *  note or callout deletes it, the way an emptied text box goes away. */
function editWords(s: Surface, item: MarkupDto): void {
  finishTyping(true);
  const pts = coordsOf(item.geometry as GeoJSON.Geometry) as LngLat[];
  const at = item.kind === 'callout' ? pts[pts.length - 1] : pts[0];
  if (!at || !WORDY.has(item.kind)) return;
  const was = (item.label ?? '').trim();
  select(s, item.id);
  startTyping(s, {
    at, kind: item.kind as 'text' | 'callout' | 'pin', itemId: item.id, value: item.label ?? '',
    align: item.kind === 'pin' ? 'center' : item.kind === 'callout' ? calloutAnchor(pts) : 'left',
    look: { color: item.color, width: item.width },
    done: (label) => {
      if (label === null || label === was) return;
      if (!label) {
        if (item.kind !== 'pin') void removeShape(s, item.id);
        return;
      }
      if (item.kind !== 'pin') rememberLabel(label, { color: item.color, width: item.width });
      // Shown at once; a refused save puts the old words back.
      s.items = s.items.map((m) => (m.id === item.id ? { ...m, label } : m));
      paint(s);
      void patchShape(s, item.id, { label }, { label: item.label });
    },
  });
}

// --- copies -------------------------------------------------------------------

function copyShape(item: MarkupDto): void {
  clipboard = { ...item, geometry: structuredClone(item.geometry) };
  toast(`Copied the ${KIND_WORDS[item.kind]} — Ctrl+V puts it under the pointer.`);
}

/** A copy of `src` put down at `at` — or, with no pointer over the map, just
 *  off the last one, so a run of pastes steps down the screen. A numbered pin
 *  takes the next number rather than repeating one. */
function placeCopy(s: Surface, src: MarkupDto, at: Px | null): MarkupDto['geometry'] {
  const geometry = at ? placeAt(src.geometry as GeoJSON.Geometry, s.map, at)
    : translateGeometry(src.geometry as GeoJSON.Geometry, s.map, DUPLICATE_OFFSET, DUPLICATE_OFFSET);
  if (src.kind === 'pin' && /^\d+$/.test(src.label ?? '')) {
    void createPin(s, { geometry, ...lookOf(src) });
  } else {
    void createShape(s, { kind: src.kind, geometry, label: src.label, ...lookOf(src) });
  }
  return geometry as MarkupDto['geometry'];
}

function pasteShape(s: Surface, at: Px | null): void {
  const src = clipboard;
  if (!src) return;
  const geometry = placeCopy(s, src, at);
  if (!at) clipboard = { ...src, geometry };
}

// --- the bar ----------------------------------------------------------------

/** [kind, glyph, name, key, how]. */
const TOOL_BUTTONS: [MarkupTool, string, string, string, string][] = [
  ['arrow', '↗', 'Arrow', 'A', 'drag from where it starts to where it points'],
  ['thrust', '➚', 'Thrust', 'W', 'drag a sweeping arrow along the axis of an advance'],
  ['stroke', '✎', 'Freehand', 'F', 'drag to sketch a line'],
  ['callout', '⌖', 'Callout', 'C', 'drag from the thing to where its words go (or just click it), then type'],
  ['pin', '①', 'Numbered pin', 'P', 'click to drop the next number'],
  ['text', 'T', 'Text', 'T', 'click where the words go and type'],
  ['box', '▭', 'Box', 'R', 'drag a rectangle'],
  ['ellipse', '◯', 'Ellipse', 'O', 'drag its bounds'],
  ['area', '⬠', 'Area', 'L', 'drag a lasso around the ground'],
];

/** One key per tool, V (or Esc) for Select. */
const TOOL_KEYS: Record<string, MarkupTool | null> = {
  v: null,
  ...Object.fromEntries(TOOL_BUTTONS.map(([kind, , , key]) => [key.toLowerCase(), kind])),
};

const ARMED_HINTS: Partial<Record<MarkupTool, string>> = {
  text: 'Text: click where the words go and type. Enter finishes, Shift+Enter starts a new line; words used before are offered as you type.',
  callout: 'Callout: drag from the thing to where its words go (or just click it), then type. On an empty box Enter reuses the marked label — the same thing marked again is a drag and a key.',
  pin: 'Numbered pin: click to drop the next number. Click a selected pin again to change what it says.',
};

const KIND_WORDS: Record<MarkupTool, string> = {
  arrow: 'arrow', thrust: 'thrust', stroke: 'line', callout: 'callout', pin: 'pin',
  text: 'note', box: 'box', ellipse: 'ellipse', area: 'area',
};

export interface MarkupOpenOpts {
  /** Where the bar mounts: the body for the cockpit, the Studio's own layer
   *  inside the Studio. */
  mount?: HTMLElement;
  /** Controls that stay clickable while the bar holds the map's input,
   *  beyond the bar itself. The Studio passes its whole overlay: it has no
   *  feed or cards for a stray click to open, and its own rail — the damage
   *  tools, Load, Swap, close — must keep working with the bar up. */
  extraControls?: () => Element[];
  /** The name of the sheet shapes go to, shown on the bar. */
  sheetLabel?: () => string;
  /** The bar's "Sheet" button: toggles the sheet flyout (frame, layers,
   *  composition, export). */
  onSheet?: () => void;
  /** Whether that flyout is open — the button reads as pressed while it is. */
  sheetOpen?: () => boolean;
  /** The bar's "Export" button: export the sheet as it is set up. */
  onExport?: () => void;
  /** The bar was closed from inside (Done). */
  onClose?: () => void;
}

interface Editor {
  s: Surface;
  rail: HTMLElement;
  tool: MarkupTool | null;
  /** A shape being stamped: each click on the map puts down a copy of it. */
  stamp: MarkupDto | null;
  opts: MarkupOpenOpts;
  /** Redraw the selection frame (after a paint, a map move). */
  refresh: () => void;
  cleanup: () => void;
}

let editor: Editor | null = null;

function buildBar(opts: MarkupOpenOpts): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pod markup-bar';
  el.innerHTML = `
    ${opts.sheetLabel ? `<div class="mk-sec">
      <button type="button" class="mk-sheet" data-mk-act="sheet"
        title="Shapes are saved to this sheet. Open it: title, frame, layers, legend, templates and export">
        <span class="mk-sheetico">▤</span><span class="mk-sheetname"></span><span class="mk-caret">▾</span>
      </button>
    </div>` : ''}
    <div class="mk-sec mk-tools">
      <button type="button" data-mk-act="select" title="Select (V) — move, resize or retype a mark; drag empty map to pan">↖ Select</button>
      ${TOOL_BUTTONS.map(([k, glyph, name, key, how]) => {
        const title = `${name} (${key}) — ${how}`;
        return `<button type="button" data-mk="${k}" title="${title}" aria-label="${title}" aria-pressed="false">${glyph}<span>${name}</span></button>`;
      }).join('')}
    </div>
    <div class="mk-sec mk-pen">
      <span class="mk-colors">${COLORS.map((c) => `<button type="button" class="mk-sw" data-color="${c}"
        style="background:${c}" title="${c}"></button>`).join('')}</span>
      <input type="range" min="1" max="10" step="0.5" value="${width}"
        title="Stroke width (text size, for a note or a pin)" />
      <button type="button" data-mk-act="dash" title="Dashed lines and outlines">┅</button>
      <button type="button" data-mk-act="fill" title="How a box, ellipse or area is filled"></button>
    </div>
    <div class="mk-sec mk-sel" hidden>
      <span class="mk-what"></span>
      <button type="button" data-mk-act="edit" title="Retype the words (Enter, or click them again)">✎</button>
      <button type="button" data-mk-act="stamp" title="Stamp copies: each click on the map puts one down (Esc stops). Ctrl+C / Ctrl+V and Alt-drag copy too">⧉</button>
      <button type="button" data-mk-act="delete" title="Delete (Del)">✕</button>
    </div>
    <div class="mk-sec mk-actions">
      <button type="button" data-mk-act="undo" title="Undo (Ctrl+Z)" aria-label="Undo">↶</button>
      <button type="button" data-mk-act="redo" title="Redo (Ctrl+Shift+Z)" aria-label="Redo">↷</button>
      ${opts.onExport ? '<button type="button" data-mk-act="export" title="Export the sheet as it is set up">↓ Export</button>' : ''}
      <button type="button" data-mk-act="done" title="Finish markup (Esc)">Done</button>
    </div>
    <div class="mk-hint" role="status"></div>`;
  return el;
}

function syncRail(): void {
  const ed = editor;
  if (!ed) return;
  const { rail, s } = ed;
  const selectButton = rail.querySelector('[data-mk-act="select"]')!;
  selectButton.classList.toggle('active', !ed.tool && !ed.stamp);
  selectButton.setAttribute('aria-pressed', String(!ed.tool && !ed.stamp));
  const stampLabel = ed.stamp?.label ? ` “${ed.stamp.label.replace(/\n+/g, ' ')}”` : '';
  const armed = TOOL_BUTTONS.find(([kind]) => kind === ed.tool);
  rail.querySelector('.mk-hint')!.textContent = ed.stamp
    ? `Stamping the ${KIND_WORDS[ed.stamp.kind]}${stampLabel}: click the map to put down a copy, as many as you need. Esc stops.`
    : armed
      ? `${ARMED_HINTS[armed[0]] ?? `${armed[2]}: ${armed[4]}.`} V or Esc returns to Select.`
      : 'Click a mark to select it: drag to move, drag its handles to reshape, Alt-drag to copy. Click selected words again (or double-click) to retype; double-click empty map to write. Ctrl+Z undoes.';
  rail.querySelectorAll<HTMLElement>('[data-mk]').forEach((b) => {
    b.classList.toggle('active', b.dataset.mk === ed.tool);
    b.setAttribute('aria-pressed', String(b.dataset.mk === ed.tool));
  });
  rail.querySelectorAll<HTMLElement>('[data-color]').forEach((b) => {
    b.classList.toggle('active', b.dataset.color === color);
  });
  const name = rail.querySelector('.mk-sheetname') as HTMLElement | null;
  if (name) name.textContent = ed.opts.sheetLabel?.() ?? '';
  const dash = rail.querySelector('[data-mk-act="dash"]') as HTMLElement;
  dash.classList.toggle('active', dashed);
  (rail.querySelector('[data-mk-act="fill"]') as HTMLElement).textContent = FILL_LABELS[fill];
  const sheetBtn = rail.querySelector('.mk-sheet') as HTMLElement | null;
  if (sheetBtn) sheetBtn.classList.toggle('active', !!ed.opts.sheetOpen?.());
  (rail.querySelector('[data-mk-act="undo"]') as HTMLButtonElement).disabled = !s.undo.length;
  (rail.querySelector('[data-mk-act="redo"]') as HTMLButtonElement).disabled = !s.redo.length;
  const sel = s.items.find((m) => m.id === s.selected) ?? null;
  const row = rail.querySelector('.mk-sel') as HTMLElement;
  row.hidden = !sel;
  if (sel) {
    (row.querySelector('.mk-what') as HTMLElement).textContent = KIND_WORDS[sel.kind] ?? sel.kind;
    (row.querySelector('[data-mk-act="edit"]') as HTMLElement).hidden = !WORDY.has(sel.kind);
    row.querySelector('[data-mk-act="stamp"]')!.classList.toggle('active', !!ed.stamp);
  }
}

/** Repaint the bar's words (a sheet was renamed or switched, the flyout
 *  opened or closed). */
export function refreshMarkupRail(): void {
  syncRail();
}

/** The bar on `map`, while it is up — the sheet flyout mounts inside it. */
export const markupBarElement = (map: maplibregl.Map): HTMLElement | null =>
  (editor?.s.map === map ? editor.rail : null);

function setTool(next: MarkupTool | null): void {
  const ed = editor;
  if (!ed) return;
  finishTyping(true);
  ed.tool = next;
  ed.stamp = null;
  ed.s.map.getCanvas().style.cursor = next ? 'crosshair' : '';
  // Drawing into a hidden layer would show nothing at all.
  if (next) setMarkupVisible(ed.s.map, true);
  syncRail();
}

function setStamp(item: MarkupDto | null): void {
  const ed = editor;
  if (!ed) return;
  ed.tool = null;
  ed.stamp = item ? { ...item } : null;
  ed.s.map.getCanvas().style.cursor = item ? 'copy' : '';
  syncRail();
}

/** Arm markup mode on a map: the rail appears and the map draws instead of
 *  selecting. One editor at a time — arming one map closes the other. */
export function openMarkup(map: maplibregl.Map, opts: MarkupOpenOpts = {}): void {
  if (editor?.s.map === map) return;
  if (editor) closeMarkup(editor.s.map);
  const s = surfaceOf(map);
  setMarkupVisible(map, true);
  const rail = buildBar(opts);
  (opts.mount ?? document.body).appendChild(rail);
  // The selection frame: DOM over the canvas, never in it.
  const chrome = document.createElement('div');
  chrome.className = 'mk-chrome';
  chrome.hidden = true;
  chrome.innerHTML = '<div class="mk-frame"></div>';
  map.getContainer().appendChild(chrome);
  const frameEl = chrome.querySelector('.mk-frame') as HTMLElement;

  const canvas = map.getCanvas();
  // A double-click on a mark retypes it, and on empty map starts a note — a
  // canvas's double-click, not the map's zoom, while the bar is up.
  const dblZoomWas = map.doubleClickZoom.isEnabled();
  map.doubleClickZoom.disable();

  const ptAt = (ev: PointerEvent | MouseEvent): Px => {
    const rect = canvas.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  };
  const hitAt = (p: Px): string | null => {
    const layers = CLICK_IDS.filter((id) => map.getLayer(id));
    const box: [maplibregl.PointLike, maplibregl.PointLike] =
      [[p.x - HIT_PX, p.y - HIT_PX], [p.x + HIT_PX, p.y + HIT_PX]];
    const hit = map.queryRenderedFeatures(box, { layers })[0];
    return (hit?.properties?.id as string) || null;
  };
  /** The mark a press at `p` takes hold of. The selected mark's words are
   *  held by their whole box, not only where a glyph is. */
  const grabbable = (p: Px): MarkupDto | undefined => {
    const sel = s.items.find((m) => m.id === s.selected);
    if (sel && WORDY.has(sel.kind)) {
      const r = wordsRect(map, sel);
      if (r && inside(r, p, FRAME_PAD)) return sel;
    }
    const id = hitAt(p);
    return id ? s.items.find((m) => m.id === id) : undefined;
  };

  let drag: { kind: MarkupTool; start: Px; end: Px; path: LngLat[]; screen: Px[];
              pointerId: number; hit: string | null } | null = null;
  let moving: { id: string; start: Px; orig: GeoJSON.Geometry; g: GeoJSON.Geometry;
                pointerId: number; moved: boolean; copy: boolean; wasSelected: boolean } | null = null;
  let resizing: { id: string; h: Handle; orig: GeoJSON.Geometry; start: Px; pointerId: number;
                  moved: boolean } | null = null;
  // This press was markup's, so the click that ends it is too.
  let handled = false;
  // Where the pointer last was over the map — where Ctrl+V puts a copy.
  let lastPt: Px | null = null;
  let hoverFrame = 0;

  // --- the selection frame and its handles ---------------------------------

  let handles: Handle[] = [];
  const handleEls: HTMLElement[] = [];
  const refresh = (): void => {
    const item = s.items.find((m) => m.id === s.selected);
    const r = item && s.visible && s.editing !== item.id ? frameOf(map, item) : null;
    if (!item || !r) { chrome.hidden = true; return; }
    chrome.hidden = false;
    const f = { x0: r.x0 - FRAME_PAD, y0: r.y0 - FRAME_PAD, x1: r.x1 + FRAME_PAD, y1: r.y1 + FRAME_PAD };
    frameEl.style.transform = `translate(${f.x0}px, ${f.y0}px)`;
    frameEl.style.width = `${f.x1 - f.x0}px`;
    frameEl.style.height = `${f.y1 - f.y0}px`;
    handles = handlesOf(item, map, f);
    // Handle elements are reused, never rebuilt: the one being dragged holds
    // the pointer capture.
    while (handleEls.length < handles.length) {
      const h = document.createElement('div');
      chrome.append(h);
      handleEls.push(h);
    }
    while (handleEls.length > handles.length) handleEls.pop()!.remove();
    handles.forEach((h, i) => {
      const el = handleEls[i]!;
      el.className = h.kind === 'vertex' ? 'mk-h v' : 'mk-h';
      el.dataset.i = String(i);
      el.dataset.c = h.kind === 'corner' ? String(h.corner) : '';
      el.style.transform = `translate(${h.at.x}px, ${h.at.y}px)`;
    });
  };

  chrome.addEventListener('pointerdown', (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>('.mk-h');
    const h = el ? handles[Number(el.dataset.i)] : undefined;
    const item = s.items.find((m) => m.id === s.selected);
    if (!el || !h || !item || ev.button === 2) return;
    ev.preventDefault();
    ev.stopPropagation();
    finishTyping(true);
    settleNudge();
    el.setPointerCapture(ev.pointerId);
    resizing = { id: item.id, h, orig: item.geometry as GeoJSON.Geometry, start: ptAt(ev),
                 pointerId: ev.pointerId, moved: false };
  });
  chrome.addEventListener('pointermove', (ev) => {
    const r = resizing;
    if (!r || ev.pointerId !== r.pointerId) return;
    const at = ptAt(ev);
    const dx = at.x - r.start.x;
    const dy = at.y - r.start.y;
    if (!r.moved && Math.hypot(dx, dy) < 2) return;
    r.moved = true;
    const g = r.h.kind === 'vertex'
      ? setVertex(r.orig, r.h.index, unproject(map, { x: r.h.at.x + dx, y: r.h.at.y + dy }))
      : scaleGeometry(r.orig, map, r.h.fixed, r.h.from, { x: r.h.from.x + dx, y: r.h.from.y + dy },
                      ev.shiftKey);
    s.items = s.items.map((m) => (m.id === r.id ? { ...m, geometry: g } : m));
    paint(s);
  });
  const endResize = (ev: PointerEvent): void => {
    const r = resizing;
    if (!r || ev.pointerId !== r.pointerId) return;
    resizing = null;
    if (!r.moved) return;
    if (ev.type === 'pointercancel') {
      s.items = s.items.map((m) => (m.id === r.id ? { ...m, geometry: r.orig } : m));
      paint(s);
      return;
    }
    const now = s.items.find((m) => m.id === r.id);
    if (now) void patchShape(s, r.id, { geometry: now.geometry }, { geometry: r.orig });
  };
  chrome.addEventListener('pointerup', endResize);
  chrome.addEventListener('pointercancel', endResize);

  // --- gestures on the map ---------------------------------------------------

  const listen = (): void => {
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
  };
  const unlisten = (): void => {
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
  };

  const onDown = (ev: PointerEvent): void => {
    const ed = editor;
    if (!ed || ed.s !== s || ev.button === 2) return;
    handled = false;
    // A press anywhere on the map finishes the words being typed, and does
    // nothing else — the way clicking away from a text box does.
    if (typing) {
      finishTyping(true);
      handled = true;
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
    settleNudge();
    // A stamp puts its copy down on the CLICK (onClick), so a drag still pans.
    if (ed.stamp) return;
    const at = ptAt(ev);
    if (!ed.tool) {
      // No tool: a press on a mark picks it up to move it (Alt: a copy of
      // it); anywhere else the map pans as usual.
      const item = grabbable(at);
      if (!item) return;
      ev.preventDefault();
      ev.stopPropagation();
      handled = true;
      const wasSelected = s.selected === item.id;
      select(s, item.id);
      canvas.setPointerCapture(ev.pointerId);
      map.dragPan.disable();
      moving = { id: item.id, start: at, orig: item.geometry as GeoJSON.Geometry,
                 g: item.geometry as GeoJSON.Geometry, pointerId: ev.pointerId, moved: false,
                 copy: ev.altKey, wasSelected };
      listen();
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    handled = true;
    canvas.setPointerCapture(ev.pointerId);
    map.dragPan.disable();
    drag = { kind: ed.tool, start: at, end: at, path: [unproject(map, at)], screen: [at],
             pointerId: ev.pointerId, hit: ed.tool === 'text' ? hitAt(at) : null };
    listen();
  };

  const onMove = (ev: PointerEvent): void => {
    if (moving && ev.pointerId === moving.pointerId) {
      const mv = moving;
      const at = ptAt(ev);
      const dx = at.x - mv.start.x;
      const dy = at.y - mv.start.y;
      if (!mv.moved && Math.hypot(dx, dy) < DRAG_MIN_PX) return;
      mv.moved = true;
      mv.g = translateGeometry(mv.orig, map, dx, dy);
      const item = s.items.find((m) => m.id === mv.id);
      if (mv.copy && item) {
        // Alt-drag: the original stays put and its copy follows the hand.
        canvas.style.cursor = 'copy';
        s.draft = { ...item, id: '', geometry: mv.g as MarkupDto['geometry'] };
      } else {
        s.items = s.items.map((m) => (m.id === mv.id ? { ...m, geometry: mv.g as MarkupDto['geometry'] } : m));
      }
      paint(s);
      return;
    }
    if (!drag || ev.pointerId !== drag.pointerId) return;
    const at = ptAt(ev);
    if (drag.kind === 'stroke' || drag.kind === 'thrust' || drag.kind === 'area') {
      const last = drag.end;
      if (Math.hypot(at.x - last.x, at.y - last.y) < STROKE_STEP_PX) return;
      drag.path.push(unproject(map, at));
      drag.screen.push(at);
    }
    drag.end = at;
    if (drag.kind === 'text' || drag.kind === 'pin') return;
    const geometry = geometryFor(map, drag.kind, drag.start, drag.end, drag.path, drag.screen);
    s.draft = geometry
      ? { id: '', kind: drag.kind, geometry, label: null, color, width, dashed, fill,
          createdAt: '', updatedAt: '' }
      : null;
    paint(s);
  };

  const onUp = (ev: PointerEvent): void => {
    if (moving && ev.pointerId === moving.pointerId) {
      const mv = moving;
      moving = null;
      unlisten();
      map.dragPan.enable();
      const item = s.items.find((m) => m.id === mv.id);
      if (!mv.moved) {
        // A second click on selected words retypes them — the double-click
        // every canvas answers, and a tap on a phone.
        if (mv.wasSelected && item && WORDY.has(item.kind) && ev.type !== 'pointercancel') {
          editWords(s, item);
        }
        return;
      }
      if (mv.copy) {
        canvas.style.cursor = '';
        // The copy stays drawn where it was dropped until it is saved.
        if (ev.type === 'pointercancel' || !item) { s.draft = null; paint(s); return; }
        void createShape(s, { kind: item.kind, geometry: mv.g, label: item.label, ...lookOf(item) });
        return;
      }
      if (ev.type === 'pointercancel') {
        s.items = s.items.map((m) => (m.id === mv.id ? { ...m, geometry: mv.orig as MarkupDto['geometry'] } : m));
        paint(s);
        return;
      }
      void patchShape(s, mv.id, { geometry: mv.g as MarkupDto['geometry'] },
                      { geometry: mv.orig as MarkupDto['geometry'] });
      return;
    }
    if (!drag || ev.pointerId !== drag.pointerId) return;
    // Pointerup may carry the last position without a preceding move event.
    if (ev.type !== 'pointercancel') onMove(ev);
    const d = drag;
    drag = null;
    unlisten();
    map.dragPan.enable();
    const moved = Math.hypot(d.end.x - d.start.x, d.end.y - d.start.y);
    s.draft = null;
    if (ev.type === 'pointercancel') { paint(s); return; }
    if (d.kind === 'text') {
      // The text tool on words already there retypes them.
      const hit = d.hit ? s.items.find((m) => m.id === d.hit) : undefined;
      if (hit && hit.kind === 'text') { editWords(s, hit); return; }
      paint(s);
      newText(s, d.start);
      return;
    }
    if (d.kind === 'pin') {
      paint(s);
      void createPin(s, { geometry: geometryFor(map, 'pin', d.start, d.end, d.path, d.screen)! });
      return;
    }
    if (d.kind === 'callout') {
      const end = moved < DRAG_MIN_PX
        ? { x: d.start.x + CALLOUT_OFFSET.x, y: d.start.y + CALLOUT_OFFSET.y } : d.end;
      newCallout(s, geometryFor(map, 'callout', d.start, end, d.path, d.screen) as GeoJSON.LineString);
      return;
    }
    paint(s);
    if (!isDrawGesture(d.kind, d.start, d.end, d.screen)) return;
    const geometry = geometryFor(map, d.kind, d.start, d.end, d.path, d.screen);
    if (geometry) void createShape(s, { kind: d.kind, geometry });
  };

  // With no tool armed a click on empty map lets go of the selection; with a
  // stamp it puts down a copy there.
  const onClick = (e: maplibregl.MapMouseEvent): void => {
    const ed = editor;
    if (!ed || ed.s !== s) return;
    if (handled) { handled = false; return; }
    if (ed.stamp) { placeCopy(s, ed.stamp, e.point); return; }
    if (ed.tool || typing) return;
    select(s, hitAt(e.point));
  };

  const onDblClick = (e: maplibregl.MapMouseEvent): void => {
    const ed = editor;
    if (!ed || ed.s !== s || ed.tool || ed.stamp || typing) return;
    // On a mark, the second click already opened its words (or it has none).
    if (grabbable(e.point)) return;
    e.preventDefault();
    newText(s, e.point);
  };

  // The pointer over the map: where a paste lands, and the cursor that says
  // a mark can be picked up. One hit test per frame at most.
  const onHover = (ev: PointerEvent): void => {
    lastPt = ptAt(ev);
    if (hoverFrame || moving || drag || resizing) return;
    hoverFrame = requestAnimationFrame(() => {
      hoverFrame = 0;
      const ed = editor;
      if (!ed || ed.s !== s || ed.tool || ed.stamp || moving || drag || !lastPt) return;
      canvas.style.cursor = grabbable(lastPt) ? 'move' : '';
    });
  };
  const onLeave = (): void => { lastPt = null; };

  const onMapMove = (): void => {
    refresh();
    if (typing?.s === s) layoutTyping(typing);
  };

  // The keys a drawing program answers. Called by the edit lock ahead of its
  // own swallowing of stray keys, and never while words are being typed.
  const onKey = (ev: KeyboardEvent): boolean => {
    const ed = editor;
    if (!ed || ed.s !== s || typing) return false;
    const sel = s.items.find((m) => m.id === s.selected) ?? null;
    const k = ev.key.toLowerCase();
    if (ev.ctrlKey || ev.metaKey) {
      if (ev.altKey) return false;
      if (k === 'z') { history(s, !ev.shiftKey); return true; }
      if (k === 'y') { history(s, false); return true; }
      if (k === 'c' && sel) { copyShape(sel); return true; }
      if (k === 'v' && clipboard) { pasteShape(s, lastPt); return true; }
      if (k === 'd' && sel) { placeCopy(s, sel, null); return true; }
      return false;
    }
    if (ev.altKey) return false;
    // Enter, Space and the arrows belong to a focused button on the bar.
    const onBar = ed.rail.contains(ev.target as Node);
    if (sel && (ev.key === 'Delete' || ev.key === 'Backspace')) { void removeShape(s, sel.id); return true; }
    if (sel && !onBar) {
      if (ev.key === 'Enter' && WORDY.has(sel.kind)) { editWords(s, sel); return true; }
      const n = ev.shiftKey ? NUDGE_BIG_PX : NUDGE_PX;
      const by: Record<string, [number, number]> = {
        ArrowLeft: [-n, 0], ArrowRight: [n, 0], ArrowUp: [0, -n], ArrowDown: [0, n],
      };
      const d = by[ev.key];
      if (d) { nudge(s, d[0], d[1]); return true; }
    }
    if (ev.shiftKey || ev.repeat || !(k in TOOL_KEYS)) return false;
    setTool(TOOL_KEYS[k] ?? null);
    return true;
  };

  const releaseEdit = beginMapEdit({ surface: canvas, controls: rail, onKey,
    extraControls: () => [...map.getContainer().querySelectorAll('.sheet-draw, .mk-chrome, .mk-type'),
                          ...(opts.extraControls?.() ?? [])],
  });

  rail.addEventListener('pointerdown', (ev) => ev.stopPropagation());
  // The pen's buttons never take focus from words being typed.
  rail.addEventListener('mousedown', (ev) => {
    if (typing && (ev.target as HTMLElement).closest('.mk-pen button')) ev.preventDefault();
  });
  rail.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest('button');
    if (!btn) return;
    ev.stopPropagation();
    const ed = editor;
    if (!ed) return;
    // The pen restyles what is being typed and hands focus back to it.
    const swatch = btn.dataset.color;
    if (swatch) {
      color = swatch;
      remember(COLOR_KEY, color);
      if (typing && !typing.itemId) restyleTyping(typing, { color });
      else if (typing) { typing.look = { ...typing.look, color }; layoutTyping(typing); }
      syncRail();
      // A swatch with something selected restyles it — the reading everyone
      // expects from picking a colour while a thing is highlighted.
      void restyle(s, { color });
      refocusTyping();
      return;
    }
    const act = btn.dataset.mkAct;
    if (act === 'dash') {
      dashed = !dashed;
      remember(DASH_KEY, dashed ? '1' : '0');
      syncRail();
      void restyle(s, { dashed });
      refocusTyping();
      return;
    }
    if (act === 'fill') {
      fill = FILLS[(FILLS.indexOf(fill) + 1) % FILLS.length]!;
      remember(FILL_KEY, fill);
      syncRail();
      const sel = s.items.find((m) => m.id === s.selected);
      if (sel && sel.geometry.type === 'Polygon') void restyle(s, { fill });
      refocusTyping();
      return;
    }
    // Everything else finishes the words first.
    finishTyping(act !== 'delete');
    const kind = btn.dataset.mk as MarkupTool | undefined;
    if (kind) { setTool(ed.tool === kind ? null : kind); return; }
    const sel = s.items.find((m) => m.id === s.selected);
    if (act === 'select') setTool(null);
    else if (act === 'delete' && sel) void removeShape(s, sel.id);
    else if (act === 'edit' && sel) editWords(s, sel);
    else if (act === 'stamp' && sel) setStamp(ed.stamp ? null : sel);
    else if (act === 'undo') history(s, true);
    else if (act === 'redo') history(s, false);
    else if (act === 'sheet') ed.opts.onSheet?.();
    else if (act === 'export') ed.opts.onExport?.();
    else if (act === 'done') {
      const onClose = ed.opts.onClose;
      closeMarkup(map);
      onClose?.();
    }
  });
  const slider = rail.querySelector('input[type="range"]') as HTMLInputElement;
  slider.addEventListener('input', () => {
    width = Number(slider.value);
    remember(WIDTH_KEY, String(width));
    if (typing && !typing.itemId) restyleTyping(typing, { width });
    else if (typing) { typing.look = { ...typing.look, width }; layoutTyping(typing); }
  });
  slider.addEventListener('change', () => {
    void restyle(s, { width });
    refocusTyping();
  });

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onHover);
  canvas.addEventListener('pointerleave', onLeave);
  map.on('click', onClick);
  map.on('dblclick', onDblClick);
  map.on('move', onMapMove);
  map.on('resize', onMapMove);

  editor = {
    s, rail, tool: null, stamp: null, opts, refresh,
    cleanup: () => {
      finishTyping(true);
      settleNudge();
      releaseEdit();
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onHover);
      canvas.removeEventListener('pointerleave', onLeave);
      if (hoverFrame) cancelAnimationFrame(hoverFrame);
      unlisten();
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      map.off('move', onMapMove);
      map.off('resize', onMapMove);
      if (dblZoomWas) map.doubleClickZoom.enable();
      map.dragPan.enable();
      canvas.style.cursor = '';
      rail.remove();
      chrome.remove();
      drag = null;
      moving = null;
      resizing = null;
      s.draft = null;
      s.selected = null;
      paint(s);
    },
  };
  syncRail();
  refresh();
  for (const cb of s.editorListeners) cb(true);
}

export function closeMarkup(map: maplibregl.Map): void {
  if (editor?.s.map !== map) return;
  const ed = editor;
  // Words half typed are kept: closing the bar is not giving them up.
  finishTyping(true);
  editor = null;
  ed.cleanup();
  for (const cb of ed.s.editorListeners) cb(false);
}

/** True while the bar is up (on `map`, when given). main.ts asks, so a markup
 *  drag doesn't also open the card of whatever entity sits under it. */
export const isMarkupActive = (map?: maplibregl.Map): boolean =>
  (map ? editor?.s.map === map : editor !== null);

/** Esc: give up the words being typed first, then a stamp, the selection, the
 *  tool, and the mode last. Returns true when it consumed the key. */
export function markupEscape(map: maplibregl.Map): boolean {
  const ed = editor;
  if (!ed || ed.s.map !== map) return false;
  if (typing) { finishTyping(false); return true; }
  if (ed.stamp) { setStamp(null); return true; }
  if (ed.s.selected) { select(ed.s, null); return true; }
  if (ed.tool) { setTool(null); return true; }
  const onClose = ed.opts.onClose;
  closeMarkup(map);
  onClose?.();
  return true;
}
